import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Clock, Flower, FlaskConical, Moon } from 'lucide-react';
import { BULK_TIERS } from '../utils/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 fade-in" data-testid="products-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
            Our Collection
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight text-brand-green-dark mb-6">
            Four Signature Lilies
          </h1>
          <p className="font-body text-base sm:text-lg leading-relaxed font-light text-brand-green-dark/80 max-w-2xl mx-auto mb-8">
            A-Grade quality blooms with beautiful aroma, expertly cut and ready for a beautiful vase or evening.
            Delivered within 2 business days for orders placed before 2 PM · Hyderabad.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-brand-green-dark bg-brand-blush/40 px-4 py-2 rounded-sm">
              <Sparkles className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm">A-Grade Quality</span>
            </div>
            <div className="flex items-center gap-2 text-brand-green-dark bg-brand-blush/40 px-4 py-2 rounded-sm">
              <Clock className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm">2-Day Delivery</span>
            </div>
            <div className="flex items-center gap-2 text-brand-green-dark bg-brand-blush/40 px-4 py-2 rounded-sm">
              <Flower className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm">Vase Ready</span>
            </div>
            <div className="flex items-center gap-2 text-brand-green-dark bg-brand-blush/40 px-4 py-2 rounded-sm">
              <FlaskConical className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm">Beautiful Aroma</span>
            </div>
            <div className="flex items-center gap-2 text-brand-green-dark bg-brand-blush/40 px-4 py-2 rounded-sm">
              <Moon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm">Evening Ready</span>
            </div>
          </div>
        </div>

        {/* Bulk pricing banner */}
        <div className="bg-brand-sage/25 border border-brand-gold/30 p-6 rounded-sm mb-16 flex flex-wrap items-center justify-center gap-4 md:gap-8">
          <p className="font-body text-brand-green-dark font-bold uppercase tracking-wider text-xs">
            Bulk Pricing:
          </p>
          {BULK_TIERS.map((tier, idx) => (
            <div key={`banner-${tier.discount}`} className="text-center" data-testid={`banner-tier-${tier.discount}`}>
              <p className="font-heading text-2xl text-brand-green">{tier.discount}% Off</p>
              <p className="font-body text-xs text-brand-green-dark/70">{tier.label}</p>
            </div>
          ))}
          <Link
            to="/bulk-orders"
            className="text-brand-gold text-sm font-body uppercase tracking-wider hover:text-brand-green-dark smooth-transition"
            data-testid="learn-bulk-link"
          >
            Learn More →
          </Link>
        </div>

        {loading ? (
          <div className="text-center text-brand-green-dark">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group"
                data-testid={`product-${product.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden mb-4 gold-border-glow bg-brand-blush/20">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover product-image-hover"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold">
                    A-Grade
                  </span>
                  <span className="text-brand-gold/50">•</span>
                  <span className="text-brand-green-dark/60 text-xs font-body">
                    {product.color}
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-light text-brand-green-dark group-hover:text-brand-gold smooth-transition mb-1">
                  {product.name}
                </h3>
                <p className="font-body text-sm text-brand-green-dark/60 italic mb-3">
                  {product.variety}
                </p>
                <p className="font-body text-brand-green-dark/70 text-sm mb-3 leading-relaxed">
                  {product.description.split('.')[0]}.
                </p>
                <p className="font-body text-brand-green-dark text-lg mb-2">
                  ₹{product.pricePerStem}<span className="text-sm text-brand-green-dark/60"> / stem</span>
                </p>
                <div className="flex items-center gap-2 text-brand-gold text-xs">
                  <Flower className="w-3 h-3" strokeWidth={1.5} />
                  <span>Ready for a beautiful vase or evening</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
